import type { Card } from "../../../shared/types.ts";
import type { SearchDoc } from "./engine.ts";
import type { SearchClient, SearchResult } from "./types.ts";
import type { Req, Res } from "./worker.ts";

// Só o que a busca usa: corta content/note (ProseMirror) e o resto antes da cópia para o worker.
// Campos das fases 3–4 com padrão: o espelho local gravado antes delas não os tem.
const slim = (c: Card): SearchDoc => ({
  id: c.id, kind: c.kind, entityType: c.entityType ?? null, title: c.title, tagIds: c.tagIds, sourceDomain: c.sourceDomain,
  noteText: c.noteText, summary: c.summary, contentText: c.contentText, ocrText: c.ocrText, objects: c.objects ?? [],
  keyPhrases: c.keyPhrases ?? [], language: c.language ?? null, palette: c.palette ?? null, dominantColor: c.dominantColor ?? null,
  isPrivate: c.isPrivate ?? false, action: c.action ?? null, completedAt: c.completedAt ?? null, spaceIds: c.spaceIds ?? [],
  createdAt: c.createdAt, bumpedAt: c.bumpedAt, deletedAt: c.deletedAt,
});

/** SearchClient real: MiniSearch num Web Worker de módulo; cada método vira uma mensagem. */
export function createWorkerSearch(): SearchClient {
  // ponytail: sem snapshot no IndexedDB; o índice de 10k cards é montado em < 2 s (web/test/search-perf.test.ts).
  const worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
  const waiting = new Map<number, { resolve: (v: never) => void; reject: (e: Error) => void }>();
  let seq = 0;
  worker.onmessage = (e: MessageEvent<Res>) => {
    const w = waiting.get(e.data.id);
    waiting.delete(e.data.id);
    if (e.data.error !== undefined) w?.reject(new Error(e.data.error));
    else w?.resolve(e.data.result as never);
  };
  worker.onerror = () => {
    for (const w of waiting.values()) w.reject(new Error("search_worker_failed"));
    waiting.clear();
  };
  const call = <T>(method: Req["method"], ...args: unknown[]) =>
    new Promise<T>((resolve, reject) => {
      const id = ++seq;
      waiting.set(id, { resolve, reject });
      worker.postMessage({ id, method, args } satisfies Req);
    });
  return {
    load: (cards, tags) => call<void>("load", cards.map(slim), tags),
    update: (changed, removedIds, tags) => call<void>("update", changed.map(slim), removedIds, tags),
    setSpaces: (spaces, spaceCards) => call<void>("setSpaces", spaces, spaceCards),
    search: (query, opts) => call<SearchResult>("search", query, { now: opts?.now, trash: opts?.trash?.map(slim) }),
    smartSpace: (spaceId, opts) => call<string[]>("smartSpace", spaceId, { now: opts?.now }),
  };
}
